/**
 * Bulk import for Business Entity Setup (CSV / XML, design manual p.4) and Individual Licensees (CSV, p.6).
 * Parsed entirely in the browser; nothing is uploaded.
 */

export type ImportFieldMap<K extends string> = Record<K, string[]>

function normalizeHeader(h: string): string {
  return h.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** Minimal RFC 4180-style CSV parser (quoted fields, escaped quotes, CRLF). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"'
        i++
      } else if (ch === '"') {
        inQuotes = false
      } else {
        field += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      row.push(field)
      field = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else {
      field += ch
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows.filter((r) => r.some((c) => c.trim().length > 0))
}

function mapRecord<K extends string>(source: Record<string, string>, fieldMap: ImportFieldMap<K>) {
  const out = {} as Record<K, string>
  for (const key of Object.keys(fieldMap) as K[]) {
    const aliases = fieldMap[key].map(normalizeHeader)
    const match = Object.keys(source).find((h) => aliases.includes(normalizeHeader(h)))
    out[key] = match ? source[match].trim() : ''
  }
  return out
}

export function recordsFromCsv<K extends string>(text: string, fieldMap: ImportFieldMap<K>): Record<K, string>[] {
  const [header, ...body] = parseCsv(text)
  if (!header) return []
  return body.map((cells) => {
    const source: Record<string, string> = {}
    header.forEach((h, i) => {
      source[h] = cells[i] ?? ''
    })
    return mapRecord(source, fieldMap)
  })
}

/** Each record element's child elements become fields, e.g. <entity><name>…</name><npn>…</npn></entity>. */
export function recordsFromXml<K extends string>(
  text: string,
  recordTag: string,
  fieldMap: ImportFieldMap<K>,
): Record<K, string>[] {
  const doc = new DOMParser().parseFromString(text, 'application/xml')
  if (doc.getElementsByTagName('parsererror').length > 0) throw new Error('The XML file could not be read.')
  return Array.from(doc.getElementsByTagName(recordTag)).map((el) => {
    const source: Record<string, string> = {}
    Array.from(el.children).forEach((child) => {
      source[child.tagName] = child.textContent ?? ''
    })
    return mapRecord(source, fieldMap)
  })
}

export async function readImportFile<K extends string>(
  file: File,
  fieldMap: ImportFieldMap<K>,
  xmlRecordTag: string,
): Promise<Record<K, string>[]> {
  const text = await file.text()
  const isXml = file.name.toLowerCase().endsWith('.xml') || text.trimStart().startsWith('<')
  return isXml ? recordsFromXml(text, xmlRecordTag, fieldMap) : recordsFromCsv(text, fieldMap)
}

export function downloadCsvTemplate(filename: string, headers: string[]) {
  const blob = new Blob([`${headers.join(',')}\n`], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export const ENTITY_IMPORT_FIELDS: ImportFieldMap<'name' | 'residentState' | 'slLicenseNumber' | 'npn' | 'fein'> = {
  name: ['Business Entity Name', 'Entity Name', 'Name'],
  residentState: ['Resident Surplus Lines License State', 'Resident State', 'State'],
  slLicenseNumber: ['Surplus Lines License Number', 'License Number', 'SL License Number'],
  npn: ['NPN', 'National Producer Number'],
  fein: ['FEIN', 'Federal EIN', 'EIN'],
}

export const INDIVIDUAL_IMPORT_FIELDS: ImportFieldMap<'fullName' | 'residentState' | 'licenseNumber' | 'npn'> = {
  fullName: ['Full Name', 'Name', 'Full Legal Name'],
  residentState: ['Resident State', 'State'],
  licenseNumber: ['License Number', 'Surplus Lines License Number'],
  npn: ['NPN', 'National Producer Number'],
}
